import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  X,
  ShoppingBag,
  MagnifyingGlass,
  CloudArrowUp,
  CircleNotch,
  CheckCircle,
  Warning,
  Lightning,
  Eye,
} from "@phosphor-icons/react";
import { t } from "../i18n";
import {
  listShopifyConnections,
  searchShopifyResources,
  applyShopifyRewrites,
  applyShopifyRewritesAuto,
} from "../lib/api";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";

const RESOURCE_TYPES = [
  { value: "product", labelKey: "res_product" },
  { value: "page", labelKey: "res_page" },
  { value: "custom_collection", labelKey: "res_custom_collection" },
  { value: "smart_collection", labelKey: "res_smart_collection" },
];

const FIELDS = [
  { value: "title", labelKey: "shopify_fields_title" },
  { value: "body_html", labelKey: "shopify_fields_body" },
  { value: "seo_title", labelKey: "shopify_fields_seo_title" },
  { value: "seo_description", labelKey: "shopify_fields_seo_desc" },
];

/**
 * Batch Apply modal. Push N rewrites in a single API call to a single
 * Shopify resource. User picks store + resource + a target field per rewrite.
 *
 * Props:
 *   open: bool
 *   onClose: () => void
 *   lang: string
 *   rewrites: [{ original_phrase, suggested_phrase }] — required, length >= 1
 */
export default function ApplyAllToShopifyModal({
  open,
  onClose,
  lang,
  rewrites,
}) {
  const [conns, setConns] = useState([]);
  const [loadingConns, setLoadingConns] = useState(false);
  const [connId, setConnId] = useState("");
  const [resourceType, setResourceType] = useState("product");
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedResource, setSelectedResource] = useState(null);
  // Per-rewrite field selection; default body_html
  const [fields, setFields] = useState({});
  const [enabled, setEnabled] = useState({});
  const [applying, setApplying] = useState(false);
  const [response, setResponse] = useState(null);
  const [autoApplying, setAutoApplying] = useState(false);
  const [autoResponse, setAutoResponse] = useState(null);

  // Load connections on open
  useEffect(() => {
    if (!open) return;
    setLoadingConns(true);
    listShopifyConnections()
      .then((list) => {
        setConns(list || []);
        if ((list || []).length && !connId) setConnId(list[0].id);
      })
      .catch((e) => toast.error(e?.response?.data?.detail || "Error"))
      .finally(() => setLoadingConns(false));
  }, [open, connId]);

  // Reset per-open
  useEffect(() => {
    if (open) {
      setSelectedResource(null);
      setResults([]);
      setQuery("");
      setResponse(null);
      setAutoResponse(null);
      const initFields = {};
      const initEnabled = {};
      (rewrites || []).forEach((_, i) => {
        initFields[i] = "body_html";
        initEnabled[i] = true;
      });
      setFields(initFields);
      setEnabled(initEnabled);
    }
  }, [open, rewrites]);

  // Debounced live search
  const runSearch = useCallback(async () => {
    if (!connId) return;
    setSearching(true);
    try {
      const list = await searchShopifyResources(connId, {
        resourceType,
        q: query,
        limit: 20,
      });
      setResults(list || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally {
      setSearching(false);
    }
  }, [connId, resourceType, query]);

  useEffect(() => {
    if (!open || !connId) return;
    const timer = setTimeout(runSearch, 300);
    return () => clearTimeout(timer);
  }, [open, connId, resourceType, query, runSearch]);

  const submit = async () => {
    if (!selectedResource || !connId) return;
    const payload = (rewrites || [])
      .map((rw, i) => ({
        field: fields[i] || "body_html",
        original_phrase: rw.original_phrase || rw.original_text || "",
        suggested_phrase: rw.suggested_phrase || rw.suggested_text || "",
        _enabled: enabled[i] !== false,
      }))
      .filter((x) => x._enabled && x.original_phrase)
      .map(({ _enabled, ...rest }) => rest);

    if (payload.length === 0) {
      toast.error(t(lang, "apply_all_no_selection"));
      return;
    }
    setApplying(true);
    setResponse(null);
    try {
      const res = await applyShopifyRewrites({
        connection_id: connId,
        resource_type: selectedResource.resource_type,
        resource_id: selectedResource.resource_id,
        rewrites: payload,
      });
      setResponse(res);
      if (res.error) {
        toast.error(res.error);
      } else if ((res.updated_fields || []).length === 0) {
        toast.error(t(lang, "apply_phrase_not_found"));
      } else {
        toast.success(t(lang, "apply_success"));
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally {
      setApplying(false);
    }
  };

  const autoSubmit = async (dryRun = false) => {
    if (!connId) return;
    const payload = (rewrites || [])
      .map((rw, i) => ({
        original_phrase: rw.original_phrase || rw.original_text || "",
        suggested_phrase: rw.suggested_phrase || rw.suggested_text || "",
        _enabled: enabled[i] !== false,
      }))
      .filter((x) => x._enabled && x.original_phrase)
      .map(({ _enabled, ...rest }) => rest);

    if (payload.length === 0) {
      toast.error(t(lang, "apply_all_no_selection"));
      return;
    }
    setAutoApplying(true);
    setAutoResponse(null);
    setResponse(null);
    try {
      const res = await applyShopifyRewritesAuto({
        connection_id: connId,
        rewrites: payload,
        dry_run: dryRun,
      });
      setAutoResponse(res);
      if (dryRun) {
        const matchCount = (res.results || []).reduce((acc, r) => acc + (r.matches || []).length, 0);
        if (matchCount > 0) {
          toast.success(`${t(lang, "auto_route_dry_run_done")} · ${matchCount}`);
        } else {
          toast.error(t(lang, "auto_route_none_found"));
        }
      } else if (res.total_applied > 0) {
        toast.success(`${t(lang, "auto_route_applied")} · ${res.total_applied}`);
      } else if (res.total_not_found === payload.length) {
        toast.error(t(lang, "auto_route_none_found"));
      } else {
        toast.error(t(lang, "auto_route_partial_or_error"));
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Error");
    } finally {
      setAutoApplying(false);
    }
  };

  if (!open) return null;

  const rewritesList = rewrites || [];
  const selectedCount = Object.values(enabled).filter((v) => v !== false).length;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-neutral-900/60 backdrop-blur-sm p-4"
      data-testid="apply-all-modal"
      onClick={onClose}
    >
      <div
        className="w-full max-w-5xl max-h-[92vh] overflow-auto bg-white border border-neutral-900 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center gap-2 px-5 py-3 border-b border-neutral-900 bg-neutral-900 text-white">
          <ShoppingBag size={14} weight="fill" />
          <div className="text-xs uppercase tracking-[0.2em] font-mono">
            {t(lang, "apply_all_modal_title")} · {rewritesList.length}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="ml-auto text-white/80 hover:text-white"
            data-testid="apply-all-modal-close"
            aria-label="close"
          >
            <X size={16} />
          </button>
        </div>

        <div className="px-5 py-4 space-y-4">
          {loadingConns ? (
            <div className="text-xs font-mono text-neutral-500">…</div>
          ) : conns.length === 0 ? (
            <div className="border border-amber-500 bg-amber-50 p-3 text-xs font-mono text-amber-800" data-testid="apply-all-no-conn">
              {t(lang, "apply_no_conn")}
            </div>
          ) : (
            <>
              {/* AUTO-ROUTE PANEL — search every phrase across the whole store */}
              <div className="border border-violet-700 bg-violet-50 p-3.5">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-8 h-8 bg-violet-700 text-white flex items-center justify-center">
                    <Lightning size={16} weight="fill" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[11px] uppercase tracking-[0.18em] font-mono font-bold text-violet-900">
                      {t(lang, "auto_route_title")}
                    </div>
                    <div className="text-[11px] font-mono text-neutral-700 mt-1">
                      {t(lang, "auto_route_hint")}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => autoSubmit(true)}
                      disabled={autoApplying || conns.length === 0 || selectedCount === 0}
                      className="inline-flex items-center gap-2 border border-violet-700 bg-white text-violet-800 hover:bg-violet-100 disabled:opacity-50 disabled:cursor-not-allowed px-3 py-2 text-[11px] uppercase tracking-widest font-medium ma-trans"
                      data-testid="apply-all-autoroute-dryrun"
                      title={t(lang, "auto_route_dry_run_hint")}
                    >
                      {autoApplying ? <CircleNotch size={12} className="animate-spin" /> : <Eye size={12} weight="bold" />}
                      {t(lang, "auto_route_dry_run_button")}
                    </button>
                    <button
                      type="button"
                      onClick={() => autoSubmit(false)}
                      disabled={autoApplying || conns.length === 0 || selectedCount === 0}
                      className="inline-flex items-center gap-2 bg-violet-700 text-white hover:bg-violet-800 disabled:opacity-50 disabled:cursor-not-allowed px-3 py-2 text-[11px] uppercase tracking-widest font-medium ma-trans"
                      data-testid="apply-all-autoroute"
                      title={t(lang, "auto_route_hint")}
                    >
                      {autoApplying ? <CircleNotch size={12} className="animate-spin" /> : <Lightning size={12} weight="fill" />}
                      {autoApplying ? t(lang, "auto_route_running") : `${t(lang, "auto_route_button")} (${selectedCount})`}
                    </button>
                  </div>
                </div>

                {autoResponse && (
                  <div className="mt-3 border-t border-violet-200 pt-3 text-[11px] font-mono space-y-1" data-testid="apply-all-autoroute-result">
                    {autoResponse.dry_run && (
                      <div className="inline-flex items-center gap-1.5 bg-violet-100 text-violet-900 px-2 py-1 text-[10px] uppercase tracking-widest font-bold border border-violet-700 mb-1">
                        <Eye size={11} weight="bold" /> {t(lang, "auto_route_dry_run_badge")}
                      </div>
                    )}
                    <div className="flex flex-wrap gap-x-4 gap-y-1">
                      {!autoResponse.dry_run && (
                        <span className="text-emerald-700">✓ {t(lang, "auto_route_applied")}: {autoResponse.total_applied}</span>
                      )}
                      {autoResponse.dry_run && (
                        <span className="text-violet-700">⌕ {t(lang, "auto_route_dry_run_matches")}: {(autoResponse.results || []).reduce((a, r) => a + (r.matches || []).length, 0)}</span>
                      )}
                      <span className="text-amber-700">⚠ {t(lang, "auto_route_not_found")}: {autoResponse.total_not_found}</span>
                      {autoResponse.total_errors > 0 && (
                        <span className="text-rose-700">✕ {t(lang, "auto_route_errors")}: {autoResponse.total_errors}</span>
                      )}
                    </div>
                    {/* Per-rewrite breakdown */}
                    <div className="mt-2 space-y-1.5 max-h-48 overflow-auto">
                      {(autoResponse.results || []).map((r, i) => (
                        <div key={i} className="text-[10px] border border-neutral-200 bg-white p-2">
                          <div className="truncate text-neutral-600 italic">"{(r.original_phrase || '').slice(0, 90)}{r.original_phrase && r.original_phrase.length > 90 ? '…' : ''}"</div>
                          {r.not_found ? (
                            <div className="text-amber-700 mt-0.5">⚠ {t(lang, "auto_route_not_found_one")}</div>
                          ) : (
                            <div className="mt-0.5 space-y-0.5">
                              {(r.matches || []).map((m, j) => (
                                <div key={j} className={m.error ? "text-rose-700" : (autoResponse.dry_run ? "text-violet-700" : "text-emerald-700")}>
                                  {m.applied ? "✓" : (m.error ? "✕" : (autoResponse.dry_run ? "⌕" : "·"))} {m.resource_type}
                                  {m.resource_title ? ` — ${m.resource_title.slice(0, 50)}` : ""}
                                  {m.field ? ` (${m.field})` : ""}
                                  {m.error ? ` — ${m.error}` : ""}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="border-t border-dashed border-neutral-300 pt-3 text-[10px] uppercase tracking-[0.2em] font-mono text-neutral-500">
                {t(lang, "auto_route_or_manual")}
              </div>

              {/* Store + resource type + search */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <label className="block text-[10px] uppercase tracking-[0.2em] font-mono text-neutral-500 mb-1.5">
                    {t(lang, "apply_modal_connection")}
                  </label>
                  <Select value={connId} onValueChange={setConnId}>
                    <SelectTrigger
                      className="h-10 w-full rounded-none border border-neutral-900 font-mono text-sm shadow-none"
                      data-testid="apply-all-conn-select"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="rounded-none border border-neutral-900 shadow-none">
                      {conns.map((c) => (
                        <SelectItem key={c.id} value={c.id} className="rounded-none font-mono">
                          {c.name} — {c.store_url}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-[10px] uppercase tracking-[0.2em] font-mono text-neutral-500 mb-1.5">
                    {t(lang, "apply_modal_resource_type")}
                  </label>
                  <Select value={resourceType} onValueChange={(v) => { setResourceType(v); setSelectedResource(null); }}>
                    <SelectTrigger
                      className="h-10 w-full rounded-none border border-neutral-900 font-mono text-sm shadow-none"
                      data-testid="apply-all-restype-select"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="rounded-none border border-neutral-900 shadow-none">
                      {RESOURCE_TYPES.map((rt) => (
                        <SelectItem key={rt.value} value={rt.value} className="rounded-none font-mono">
                          {t(lang, rt.labelKey)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-[10px] uppercase tracking-[0.2em] font-mono text-neutral-500 mb-1.5">
                    {t(lang, "apply_modal_search")}
                  </label>
                  <div className="flex items-center border border-neutral-900 bg-white">
                    <MagnifyingGlass size={14} className="ml-3 text-neutral-500" />
                    <input
                      type="text"
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                      placeholder={t(lang, "apply_modal_search_ph")}
                      className="flex-1 px-3 py-2 text-sm font-mono outline-none bg-transparent"
                      data-testid="apply-all-search-input"
                    />
                    {searching && <CircleNotch size={14} className="mr-3 animate-spin text-neutral-500" />}
                  </div>
                </div>
              </div>

              {/* Results list */}
              <div className="border border-neutral-300 bg-white max-h-44 overflow-auto" data-testid="apply-all-results-list">
                {results.length === 0 && !searching && (
                  <div className="p-3 text-[11px] font-mono text-neutral-500">
                    {t(lang, "apply_modal_no_results")}
                  </div>
                )}
                {results.map((r) => {
                  const isSelected = selectedResource?.resource_id === r.resource_id
                    && selectedResource?.resource_type === r.resource_type;
                  return (
                    <button
                      type="button"
                      key={`${r.resource_type}:${r.resource_id}`}
                      onClick={() => setSelectedResource(r)}
                      className={`w-full text-left px-3 py-2 border-t border-neutral-100 ma-trans ${
                        isSelected
                          ? "bg-emerald-50 border-l-4 border-l-emerald-600"
                          : "hover:bg-neutral-50"
                      }`}
                      data-testid={`apply-all-result-${r.resource_id}`}
                    >
                      <div className="text-sm text-neutral-900 truncate">{r.title || "—"}</div>
                      <div className="text-[10px] font-mono text-neutral-500 truncate">
                        {r.handle ? `/${r.handle}` : `#${r.resource_id}`}
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* Rewrites list with per-row field selector */}
              {selectedResource && (
                <div className="border border-neutral-900" data-testid="apply-all-rewrites">
                  <div className="flex items-center gap-2 bg-neutral-900 text-white px-3 py-2 text-[10px] font-mono uppercase tracking-widest">
                    <span>{t(lang, "apply_all_rewrites_header")}</span>
                    <span className="ml-auto">{selectedCount}/{rewritesList.length}</span>
                  </div>
                  <div className="max-h-72 overflow-auto">
                    {rewritesList.map((rw, i) => {
                      const orig = rw.original_phrase || rw.original_text || "";
                      const sugg = rw.suggested_phrase || rw.suggested_text || "";
                      const isOn = enabled[i] !== false;
                      return (
                        <div
                          key={i}
                          className={`grid grid-cols-12 gap-2 px-3 py-2 border-t border-neutral-100 ${isOn ? "" : "opacity-50"}`}
                          data-testid={`apply-all-row-${i}`}
                        >
                          <div className="col-span-1 flex items-start justify-center pt-1">
                            <input
                              type="checkbox"
                              checked={isOn}
                              onChange={(e) => setEnabled({ ...enabled, [i]: e.target.checked })}
                              data-testid={`apply-all-toggle-${i}`}
                              className="w-4 h-4 accent-emerald-700"
                            />
                          </div>
                          <div className="col-span-7 min-w-0">
                            <div className="text-[10px] font-mono uppercase tracking-widest text-red-700">{t(lang, "original")}</div>
                            <div className="text-xs font-mono text-red-900 truncate" title={orig}>{orig}</div>
                            <div className="text-[10px] font-mono uppercase tracking-widest text-emerald-700 mt-1">{t(lang, "suggested")}</div>
                            <div className="text-xs font-mono text-emerald-900 truncate" title={sugg}>{sugg}</div>
                          </div>
                          <div className="col-span-4">
                            <label className="block text-[9px] uppercase tracking-[0.2em] font-mono text-neutral-500 mb-1">
                              {t(lang, "apply_modal_field")}
                            </label>
                            <Select
                              value={fields[i] || "body_html"}
                              onValueChange={(v) => setFields({ ...fields, [i]: v })}
                            >
                              <SelectTrigger
                                className="h-8 w-full rounded-none border border-neutral-900 font-mono text-xs shadow-none"
                                data-testid={`apply-all-field-${i}`}
                              >
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent className="rounded-none border border-neutral-900 shadow-none">
                                {FIELDS.map((f) => (
                                  <SelectItem key={f.value} value={f.value} className="rounded-none font-mono text-xs">
                                    {t(lang, f.labelKey)}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Result banner */}
              {response && (
                <div
                  className={`border p-3 text-xs leading-relaxed ${
                    response.error || (response.updated_fields || []).length === 0
                      ? "border-amber-500 bg-amber-50 text-amber-900"
                      : "border-emerald-500 bg-emerald-50 text-emerald-900"
                  }`}
                  data-testid="apply-all-result-banner"
                >
                  <div className="flex items-center gap-2 font-mono uppercase tracking-widest text-[10px]">
                    {response.error || (response.updated_fields || []).length === 0
                      ? <><Warning size={12} weight="fill" /> {t(lang, "apply_warn_title")}</>
                      : <><CheckCircle size={12} weight="fill" /> {t(lang, "apply_done_title")}</>}
                  </div>
                  <div className="mt-1">
                    {response.error
                      || ((response.updated_fields || []).length > 0
                          ? `${t(lang, "apply_updated_fields")}: ${response.updated_fields.join(", ")}`
                          : t(lang, "apply_phrase_not_found"))}
                  </div>
                  {response.replacements_count && Object.keys(response.replacements_count).length > 0 && (
                    <div className="mt-1.5 text-[11px] font-mono">
                      {t(lang, "apply_replacements_count")}: {Object.entries(response.replacements_count).map(([f, n]) => `${f}=${n}`).join(", ")}
                    </div>
                  )}
                  {response.skipped && response.skipped.length > 0 && (
                    <div className="mt-1.5 text-[11px] font-mono opacity-75">
                      {t(lang, "apply_all_skipped")}: {response.skipped.length}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center gap-2 px-5 py-3 border-t border-neutral-200 bg-neutral-50">
          <button
            type="button"
            onClick={onClose}
            className="border border-neutral-900 bg-white text-neutral-900 hover:bg-neutral-100 px-4 py-2 text-xs uppercase tracking-widest font-mono ma-trans"
            data-testid="apply-all-cancel"
          >
            {t(lang, "cancel")}
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={!selectedResource || applying || conns.length === 0 || selectedCount === 0}
            className="ml-auto inline-flex items-center gap-2 bg-emerald-700 text-white hover:bg-emerald-800 disabled:opacity-50 disabled:cursor-not-allowed px-4 py-2 text-xs uppercase tracking-widest font-medium ma-trans"
            data-testid="apply-all-submit"
          >
            {applying ? <CircleNotch size={12} className="animate-spin" /> : <CloudArrowUp size={12} />}
            {applying ? t(lang, "apply_modal_applying") : `${t(lang, "apply_all_modal_submit")} (${selectedCount})`}
          </button>
        </div>
      </div>
    </div>
  );
}
